import React from 'react';
import { CheckCircle, X, CheckSquare, Square } from 'phosphor-react';

const itemBase =
  'flex items-center gap-3 py-3.5 px-4 rounded-lg border-2 transition-all duration-200 cursor-default';

const itemVariants = {
  'correct-selected':
    'bg-emerald-500/10 border-emerald-500 text-emerald-800 dark:text-emerald-400',
  'incorrect-selected':
    'bg-red-500/10 border-red-500 text-red-800 dark:text-red-400',
  'correct-not-selected':
    'bg-emerald-500/5 border-emerald-500/30 text-emerald-800 opacity-80 dark:text-emerald-400',
  neutral: 'bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-secondary)]',
};

const labelBase = 'px-2.5 py-1 rounded text-xs font-medium';

/**
 * Component to render a single option item for MultipleCorrectAnswer review
 * Shows 4 states: user picked correct, user picked wrong, missed correct, correctly avoided wrong
 */
export function MultipleCorrectOptionItem({ option, isUserSelection, isCorrectAnswer }) {
  let variant = 'neutral';
  let displayElement = null;
  let checkboxIcon = null;

  if (isUserSelection && isCorrectAnswer) {
    variant = 'correct-selected';
    checkboxIcon = <CheckSquare size={24} weight="fill" className="text-emerald-500 shrink-0" />;
    displayElement = (
      <div className="flex items-center gap-2 shrink-0">
        <span className={`${labelBase} bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}>
          Your Answer
        </span>
        <CheckCircle size={20} weight="fill" className="text-emerald-500" />
      </div>
    );
  } else if (isUserSelection && !isCorrectAnswer) {
    variant = 'incorrect-selected';
    checkboxIcon = <CheckSquare size={24} weight="fill" className="text-red-500 shrink-0" />;
    displayElement = (
      <div className="flex items-center gap-2 shrink-0">
        <span className={`${labelBase} bg-red-500/15 text-red-600 dark:text-red-400`}>
          Your Answer
        </span>
        <X size={20} weight="fill" className="text-red-500" />
      </div>
    );
  } else if (!isUserSelection && isCorrectAnswer) {
    variant = 'correct-not-selected';
    checkboxIcon = <Square size={24} weight="regular" className="text-gray-400 shrink-0" />;
    displayElement = (
      <div className="flex items-center gap-2 shrink-0">
        <span className={`${labelBase} bg-emerald-500/15 text-emerald-600 dark:text-emerald-400`}>
          Correct
        </span>
        <CheckCircle size={20} weight="regular" className="text-emerald-500" />
      </div>
    );
  } else {
    variant = 'neutral';
    checkboxIcon = <Square size={24} weight="regular" className="text-gray-400 shrink-0" />;
  }

  return (
    <div key={option.id} className={`${itemBase} ${itemVariants[variant]}`}>
      <div className="flex-1 flex items-center gap-3">
        {checkboxIcon}
        <span className="text-[15px] leading-relaxed">{option.text}</span>
      </div>
      {displayElement}
    </div>
  );
}
